import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Flame, Snowflake, Trophy, Table2, Zap } from "lucide-react";
import { PowerRankings } from "@/components/PowerRankings";
import { HeatersButton, SlumpsButton } from "@/components/HeatersButton";
import { api, errMsg } from "@/lib/api";
import { SPORT_META } from "@/lib/odds";
import { useFavoriteSports } from "@/components/SportTabs";
import { useDataSource, visibleSports } from "@/lib/dataSource";
import { StandingsTable, FormDots, StreakChipText } from "@/components/StandingsTable";
import { OffSeasonNotice } from "@/components/OffSeasonNotice";

const SPORTS = Object.keys(SPORT_META).filter((k) => k !== "horse_racing");

function HeatList({ title, rows, hot }) {
  const Icon = hot ? Flame : Snowflake;
  return (
    <div className="glass-card p-4" data-testid={hot ? "standings-hot" : "standings-cold"}>
      <p className={`label-caps flex items-center gap-1.5 ${hot ? "text-orange-300" : "text-sky-300"}`}><Icon size={13} /> {title}</p>
      {rows.length ? (
        <ul className="mt-3 space-y-2">{rows.map((r) => <li key={r.team} className="flex items-center justify-between gap-3 text-sm"><span className="min-w-0 truncate text-white"><span className="font-mono-num mr-2 text-slate-500">#{r.rank}</span>{r.team}</span><span className="flex items-center gap-3"><FormDots form={r.form} /><StreakChipText streak={r.streak} /></span></li>)}</ul>
      ) : <p className="mt-3 text-xs text-slate-500">Nobody on a {hot ? "4-of-5 heater" : "cold spell"} right now.</p>}
    </div>
  );
}

export default function Standings() {
  const [params, setParams] = useSearchParams();
  const favorites = useFavoriteSports();
  const ds = useDataSource();
  const ordered = visibleSports([...favorites.filter((k) => SPORTS.includes(k)), ...SPORTS.filter((k) => !favorites.includes(k))], ds);
  const sport = SPORTS.includes(params.get("sport")) ? params.get("sport") : ordered[0];
  const view = params.get("view") === "power" ? "power" : "table";
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setD(null);
    api.get(`/standings/${sport}`).then((r) => setD(r.data)).catch((e) => setErr(errMsg(e)));
  }, [sport]);
  return (
    <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="standings-page">
      <p className="label-caps flex items-center gap-2 text-emerald-400"><Trophy size={14} /> League standings & form guide</p>
      <h1 className="font-display mt-2 text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Who's hot before you bet</h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400">Every league table is built from the season's settled games — the same finals you see in the Game Center. Form shows the last five results, newest on the right.</p>
      <div className="mt-6 flex gap-2 overflow-x-auto pb-1" data-testid="standings-sport-tabs">
        {ordered.map((k) => (
          <button key={k} data-testid={`standings-tab-${k}`} onClick={() => setParams({ sport: k, view })}
            className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${sport === k ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] bg-[#121824] text-slate-400 hover:border-slate-500 hover:text-white"}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: SPORT_META[k].color }} />{SPORT_META[k].label}
          </button>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-full border border-[#26334D] bg-[#121824] p-1" data-testid="standings-view-toggle">
          {[["table", "League table", Table2], ["power", "Power rankings", Zap]].map(([k, l, Icon]) => (
            <button key={k} data-testid={`standings-view-${k}`} onClick={() => setParams({ sport, view: k })} className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${view === k ? "bg-emerald-500 text-[#0B0E14]" : "text-slate-400 hover:text-white"}`}><Icon size={14} /> {l}</button>
          ))}
        </div>
        <HeatersButton />
        <SlumpsButton />
      </div>
      {view === "power" && <div className="fade-up mt-6"><PowerRankings sport={sport} /></div>}
      {view === "table" && err && <p className="mt-6 text-sm text-red-400" data-testid="standings-error">{err}</p>}
      {view === "table" && !d && !err && <div className="glass-card mt-6 h-64 animate-pulse" data-testid="standings-loading" />}
      {view === "table" && d && (
        <div className="fade-up mt-6 space-y-6">
          {d.games === 0 && <OffSeasonNotice sport={sport} context="standings" onBrowse={() => setParams({ sport: ordered.find((k) => ds.seasons?.[k]?.status === "live") || ordered[0], view })} />}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500" data-testid="standings-meta">
            <span className="font-display text-lg font-bold uppercase text-white">{d.league} {d.kind === "rankings" ? "rankings" : "table"}</span>
            <span><span className="font-mono-num text-white">{d.games}</span> games settled since {d.since}</span>
            {d.points_system && <span>{d.points_system.win} pts a win{d.draws ? `, ${d.points_system.draw} a draw` : ""}</span>}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <HeatList title="Hottest right now" rows={d.hot} hot />
            <HeatList title="Coldest right now" rows={d.cold} />
          </div>
          <StandingsTable d={d} />
          {d.recent.length > 0 && (
            <div className="glass-card p-4" data-testid="standings-recent">
              <p className="label-caps">Latest results</p>
              <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2 lg:grid-cols-3">{d.recent.map((g) => <li key={g.event_id}><Link to={`/games/${g.event_id}`} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-slate-300 hover:bg-[#182030]"><span className="truncate">{g.away} @ {g.home}</span><span className="font-mono-num shrink-0 text-white">{g.away_score}–{g.home_score}</span></Link></li>)}</ul>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
